import { Component, OnInit } from '@angular/core'
import { Firestore, collection, query, orderBy, getDocs, limit } from '@angular/fire/firestore'
import { BackendService } from 'src/app/services/backend.service'
import { GlobalService } from 'src/app/services/global.service'

const AD_CONVERSIONS_COLLECTION = 'adConversions'

@Component({
    selector: 'app-admin-ad-conversions',
    templateUrl: './admin-ad-conversions.component.html',
    styleUrls: ['./admin-ad-conversions.component.scss'],
})
export class AdminAdConversionsComponent implements OnInit {
    conversions: any[] = []
    filteredConversions: any[] = []
    loading = true
    statusFilter = 'all'
    maxRecords = 500

    // Stats
    totalValue = 0
    pendingCount = 0
    uploadedCount = 0
    failedCount = 0
    withGclidCount = 0
    acceptedCount = 0
    reviewCount = 0
    verificationMessage = ''
    loadError = ''

    constructor(
        private firestore: Firestore,
        private backendService: BackendService,
        public globalService: GlobalService
    ) {}

    async ngOnInit() {
        await this.loadConversions()
    }

    async loadConversions() {
        this.loading = true
        this.loadError = ''
        try {
            const conversionsQuery = query(
                collection(this.firestore, AD_CONVERSIONS_COLLECTION),
                orderBy('createdAt', 'desc'),
                limit(this.maxRecords)
            )
            const snapshot = await getDocs(conversionsQuery)
            this.conversions = snapshot.docs.map((doc) => {
                const data = doc.data()
                return {
                    $key: doc.id,
                    diagnostics: JSON.stringify({ errors: data['processingErrors'] || [], warnings: data['processingWarnings'] || [], fieldWarnings: data['uploadFieldWarnings'] || [] }),
                    ...data,
                    conversionValueRands: ((data['conversionValueCents'] || 0) / 100).toFixed(2),
                    createdAtDate: data['createdAt']?.toDate?.() || null,
                    uploadedAtDate: data['uploadedAt']?.toDate?.() || null,
                    conversionTimestampDate: data['conversionTimestamp']?.toDate?.() || null,
                }
            })
            this.calculateStats()
            this.applyFilter()
        } catch (error) {
            this.loadError = 'Could not load conversion records. Check administrator access and the deployed Firestore rules/indexes.'
            console.error('[Ad Conversions] Error loading conversions:', error)
        } finally {
            this.loading = false
        }
    }

    calculateStats() {
        this.totalValue = this.conversions.reduce((sum, c) => sum + (c.conversionValueCents || 0), 0) / 100
        this.pendingCount = this.conversions.filter((c) => c.uploadStatus === 'pending').length
        this.uploadedCount = this.conversions.filter((c) => c.processingStatus === 'SUCCESS').length
        this.acceptedCount = this.conversions.filter((c) => c.uploadStatus === 'accepted' || (c.uploadStatus === 'uploaded' && !c.processingStatus)).length
        this.reviewCount = this.conversions.filter((c) => c.uploadStatus === 'review' || c.statusCheckError || c.processingWarnings?.length || c.uploadFieldWarnings?.length).length
        this.failedCount = this.conversions.filter((c) => c.uploadStatus === 'failed').length
        this.withGclidCount = this.conversions.filter((c) => !!c.gclid).length
    }

    applyFilter() {
        if (this.statusFilter === 'all') {
            this.filteredConversions = this.conversions
        } else if (this.statusFilter === 'review') {
            this.filteredConversions = this.conversions.filter(c => c.uploadStatus === 'review' || c.statusCheckError || c.processingWarnings?.length || c.uploadFieldWarnings?.length)
        } else if (this.statusFilter === 'uploaded') {
            this.filteredConversions = this.conversions.filter(c => c.processingStatus === 'SUCCESS')
        } else {
            this.filteredConversions = this.conversions.filter((c) => c.uploadStatus === this.statusFilter)
        }
    }

    setFilter(status: string) {
        this.statusFilter = status
        this.applyFilter()
    }

    async verifyUploads() {
        this.loading = true
        try {
            const result = await this.backendService.verifyGoogleAdsConversions()
            this.verificationMessage = `${result.succeeded} requests processed successfully; ${result.processing} processing; ${result.review} need review; ${result.checkErrors} status checks failed.`
            await this.loadConversions()
        } catch (error: any) {
            this.verificationMessage = error.message || 'Could not verify processing status.'
        } finally { this.loading = false }
    }

    exportGoogleAdsCsv() {
        const headers = [
            'Google Click ID',
            'Conversion Name',
            'Conversion Time',
            'Conversion Value',
            'Conversion Currency',
            'Order ID',
        ]
        const rows = this.filteredConversions.filter(c => c.gclid && c.conversionTimestampDate).map((c) => [
            c.gclid || '',
            'Offline Subscription Active',
            c.conversionTimestampDate ? this.formatCsvDate(c.conversionTimestampDate) : '',
            c.conversionValueRands,
            c.currency || 'ZAR',
            c.transactionId || c.subscriptionKey,
        ])

        const csvContent = [headers, ...rows]
            .map((row) => row.map((field: string) => `"${String(field).replace(/"/g, '""')}"`).join(','))
            .join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        const date = new Date().toISOString().split('T')[0]
        a.href = url
        a.download = `${date} ParkUpp Google Ads Conversions.csv`
        a.click()
        window.URL.revokeObjectURL(url)
    }

    private formatCsvDate(date: Date): string {
        return date.toISOString().slice(0, 19).replace('T', ' ') + '+0000'
    }
}
